import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FaSearch, FaSync, FaEye, FaEyeSlash, FaFileExcel, FaCopy } from 'react-icons/fa';
import { toast } from 'react-toastify';
import * as XLSX from 'xlsx';
import './TradingAccounts.css';

const TradingAccounts = () => {
  const [accounts, setAccounts] = useState([]);
  const [filteredAccounts, setFilteredAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [accountsPerPage] = useState(10);
  const [showPasswords, setShowPasswords] = useState({});
  const [filterLeverage, setFilterLeverage] = useState('All');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const API_URL = 'https://server.account.majestyfx.com/admin_fetch_liveaccounts_list.php';

  useEffect(() => {
    fetchTradingAccounts();
  }, []);

  useEffect(() => {
    filterAndSortAccounts();
  }, [searchTerm, filterLeverage, sortConfig, accounts, startDate, endDate]);

  const fetchTradingAccounts = async () => {
    try {
      setLoading(true);
      const response = await axios.get(API_URL);
      
      if (response.data.success) {
        setAccounts(response.data.data || []);
        setFilteredAccounts(response.data.data || []);
      } else {
        toast.error('Failed to fetch trading accounts');
      }
    } catch (error) {
      console.error('Error fetching trading accounts:', error);
      toast.error('Error loading trading accounts');
    } finally {
      setLoading(false);
    }
  };

  const filterAndSortAccounts = () => {
    let filtered = accounts.filter(account => {
      const matchesSearch = 
        account.account_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        account.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        account.trade_id?.includes(searchTerm);
      
      const matchesLeverage = filterLeverage === 'All' || account.leverage === filterLeverage;
      
      let matchesDate = true;
      if (startDate || endDate) {
        const accountDate = new Date(account.Registered_Date);
        if (startDate) {
          const start = new Date(startDate);
          start.setHours(0, 0, 0, 0);
          matchesDate = matchesDate && accountDate >= start;
        }
        if (endDate) {
          const end = new Date(endDate);
          end.setHours(23, 59, 59, 999);
          matchesDate = matchesDate && accountDate <= end;
        }
      }
      
      return matchesSearch && matchesLeverage && matchesDate;
    });

    // Sorting
    if (sortConfig.key) {
      filtered.sort((a, b) => {
        const aValue = a[sortConfig.key];
        const bValue = b[sortConfig.key];
        
        if (aValue < bValue) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }

    setFilteredAccounts(filtered);
    setCurrentPage(1);
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const togglePasswordVisibility = (tradeId, type) => {
    setShowPasswords(prev => ({
      ...prev,
      [`${tradeId}_${type}`]: !prev[`${tradeId}_${type}`]
    }));
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied to clipboard!`);
  };

  const getUniqueLeverages = () => {
    const leverages = [...new Set(accounts.map(acc => acc.leverage))];
    return leverages.sort((a, b) => parseInt(a) - parseInt(b));
  };

  const exportToExcel = () => {
    if (filteredAccounts.length === 0) {
      toast.error('No data to export');
      return;
    }

    const exportData = filteredAccounts.map((account, idx) => ({
      '#': idx + 1,
      'Account Name': account.account_name,
      'Email': account.email,
      'Trade ID': account.trade_id,
      'Trader Password': account.trader_pwd,
      'Investor Password': account.invester_pwd,
      'Leverage': account.leverage,
      'Equity': account.equity,
      'Registered Date': account.Registered_Date
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Trading Accounts');
    XLSX.writeFile(wb, `Trading_Accounts_${new Date().toISOString().split('T')[0]}.xlsx`);
    toast.success('Excel file exported successfully');
  };

  // Pagination
  const indexOfLastAccount = currentPage * accountsPerPage;
  const indexOfFirstAccount = indexOfLastAccount - accountsPerPage;
  const currentAccounts = filteredAccounts.slice(indexOfFirstAccount, indexOfLastAccount);
  const totalPages = Math.ceil(filteredAccounts.length / accountsPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  return (
    <div className="trading-accounts-container">
      {/* Header */}
      <div className="page-header">
        <div className="header-left">
          <h1 className="page-title">Trading Accounts</h1>
          <p className="page-subtitle">Manage all live trading accounts</p>
        </div>
        <div className="header-right">
          <button 
            className="btn-refresh" 
            onClick={fetchTradingAccounts}
            disabled={loading}
          >
            <FaSync className={loading ? 'spinning' : ''} />
            {loading ? 'Loading...' : 'Refresh'}
          </button>
          <button 
            className="btn-export" 
            onClick={exportToExcel}
            disabled={loading || filteredAccounts.length === 0}
          >
            <FaFileExcel />
            Export to Excel
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon blue">
            <i className="fas fa-users"></i>
          </div>
          <div className="stat-details">
            <p className="stat-label">Total Accounts</p>
            <h3 className="stat-value">{accounts.length}</h3>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon green">
            <i className="fas fa-filter"></i>
          </div>
          <div className="stat-details">
            <p className="stat-label">Filtered Results</p>
            <h3 className="stat-value">{filteredAccounts.length}</h3>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon orange">
            <i className="fas fa-chart-line"></i>
          </div>
          <div className="stat-details">
            <p className="stat-label">Total Equity</p>
            <h3 className="stat-value">
              ${accounts.reduce((sum, acc) => sum + parseFloat(acc.equity || 0), 0).toFixed(2)}
            </h3>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon purple">
            <i className="fas fa-calendar"></i>
          </div>
          <div className="stat-details">
            <p className="stat-label">Active Today</p>
            <h3 className="stat-value">
              {accounts.filter(acc => {
                const today = new Date().toISOString().split('T')[0];
                return acc.Registered_Date?.startsWith(today);
              }).length}
            </h3>
          </div>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="filters-section">
        <div className="search-box">
          <FaSearch className="search-icon" />
          <input
            type="text"
            placeholder="Search by name, email, or trade ID..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
        
        <div className="filter-group">
          <label className="filter-label">Leverage:</label>
          <select
            value={filterLeverage}
            onChange={(e) => setFilterLeverage(e.target.value)}
            className="filter-select"
          >
            <option value="All">All Leverages</option>
            {getUniqueLeverages().map(leverage => (
              <option key={leverage} value={leverage}>1:{leverage}</option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <label className="filter-label">From:</label>
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="filter-select"
          />
        </div>

        <div className="filter-group">
          <label className="filter-label">To:</label>
          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className="filter-select"
          />
        </div>

        <div className="results-info">
          Showing {indexOfFirstAccount + 1} to {Math.min(indexOfLastAccount, filteredAccounts.length)} of {filteredAccounts.length} accounts
        </div>
      </div>

      {/* Table */}
      <div className="table-card">
        {loading ? (
          <div className="loading-state">
            <div className="spinner"></div>
            <p>Loading trading accounts...</p>
          </div>
        ) : filteredAccounts.length === 0 ? (
          <div className="empty-state">
            <i className="fas fa-inbox empty-icon"></i>
            <h3>No Accounts Found</h3>
            <p>No trading accounts match your current filters.</p>
            {(searchTerm || filterLeverage !== 'All' || startDate || endDate) && (
              <button 
                className="btn-clear-filters"
                onClick={() => {
                  setSearchTerm('');
                  setFilterLeverage('All');
                  setStartDate('');
                  setEndDate('');
                }}
              >
                Clear Filters
              </button>
            )}
          </div>
        ) : (
          <>
            <div className="table-wrapper">
              <table className="trading-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th onClick={() => handleSort('account_name')} className="sortable">
                      Account Name
                      {sortConfig.key === 'account_name' && (
                        <i className={`fas fa-sort-${sortConfig.direction === 'asc' ? 'up' : 'down'}`}></i>
                      )}
                    </th>
                    <th onClick={() => handleSort('email')} className="sortable">
                      Email
                      {sortConfig.key === 'email' && (
                        <i className={`fas fa-sort-${sortConfig.direction === 'asc' ? 'up' : 'down'}`}></i>
                      )}
                    </th>
                    <th onClick={() => handleSort('trade_id')} className="sortable">
                      Trade ID
                      {sortConfig.key === 'trade_id' && (
                        <i className={`fas fa-sort-${sortConfig.direction === 'asc' ? 'up' : 'down'}`}></i>
                      )}
                    </th>
                    <th>Trader Password</th>
                    <th>Investor Password</th>
                    <th onClick={() => handleSort('leverage')} className="sortable">
                      Leverage
                      {sortConfig.key === 'leverage' && (
                        <i className={`fas fa-sort-${sortConfig.direction === 'asc' ? 'up' : 'down'}`}></i>
                      )}
                    </th>
                    <th onClick={() => handleSort('equity')} className="sortable">
                      Equity
                      {sortConfig.key === 'equity' && (
                        <i className={`fas fa-sort-${sortConfig.direction === 'asc' ? 'up' : 'down'}`}></i>
                      )}
                    </th>
                    <th onClick={() => handleSort('Registered_Date')} className="sortable">
                      Registered Date
                      {sortConfig.key === 'Registered_Date' && (
                        <i className={`fas fa-sort-${sortConfig.direction === 'asc' ? 'up' : 'down'}`}></i>
                      )}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {currentAccounts.map((account, index) => (
                    <tr key={account.trade_id}>
                      <td>{indexOfFirstAccount + index + 1}</td>
                      <td className="account-name">{account.account_name}</td>
                      <td className="email-cell">
                        <span className="email-text">{account.email}</span>
                        <button
                          className="copy-btn"
                          onClick={() => copyToClipboard(account.email, 'Email')}
                          title="Copy email"
                        >
                          <FaCopy />
                        </button>
                      </td>
                      <td>
                        <span className="trade-id-badge">{account.trade_id}</span>
                      </td>
                      <td>
                        <div className="password-field">
                          <input
                            type={showPasswords[`${account.trade_id}_trader`] ? 'text' : 'password'}
                            value={account.trader_pwd}
                            readOnly
                            className="password-input"
                          />
                          <button
                            className="toggle-pwd-btn"
                            onClick={() => togglePasswordVisibility(account.trade_id, 'trader')}
                          >
                            {showPasswords[`${account.trade_id}_trader`] ? <FaEyeSlash /> : <FaEye />}
                          </button>
                          <button
                            className="copy-btn"
                            onClick={() => copyToClipboard(account.trader_pwd, 'Trader Password')}
                          >
                            <FaCopy />
                          </button>
                        </div>
                      </td>
                      <td>
                        <div className="password-field">
                          <input
                            type={showPasswords[`${account.trade_id}_investor`] ? 'text' : 'password'}
                            value={account.invester_pwd}
                            readOnly
                            className="password-input"
                          />
                          <button
                            className="toggle-pwd-btn"
                            onClick={() => togglePasswordVisibility(account.trade_id, 'investor')}
                          >
                            {showPasswords[`${account.trade_id}_investor`] ? <FaEyeSlash /> : <FaEye />}
                          </button>
                          <button
                            className="copy-btn"
                            onClick={() => copyToClipboard(account.invester_pwd, 'Investor Password')}
                          >
                            <FaCopy />
                          </button>
                        </div>
                      </td>
                      <td>
                        <span className="leverage-badge">1:{account.leverage}</span>
                      </td>
                      <td className="equity-cell">
                        ${parseFloat(account.equity || 0).toFixed(2)}
                      </td>
                      <td className="date-cell">
                        {new Date(account.Registered_Date).toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="pagination">
                <button
                  className="page-btn"
                  onClick={() => paginate(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  Previous
                </button>
                
                <div className="page-numbers">
                  {[...Array(totalPages)].map((_, index) => {
                    const pageNumber = index + 1;
                    if (
                      pageNumber === 1 ||
                      pageNumber === totalPages ||
                      (pageNumber >= currentPage - 1 && pageNumber <= currentPage + 1)
                    ) {
                      return (
                        <button
                          key={pageNumber}
                          className={`page-number ${currentPage === pageNumber ? 'active' : ''}`}
                          onClick={() => paginate(pageNumber)}
                        >
                          {pageNumber}
                        </button>
                      );
                    } else if (pageNumber === currentPage - 2 || pageNumber === currentPage + 2) {
                      return <span key={pageNumber} className="page-ellipsis">...</span>;
                    }
                    return null;
                  })}
                </div>

                <button
                  className="page-btn"
                  onClick={() => paginate(currentPage + 1)}
                  disabled={currentPage === totalPages}
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default TradingAccounts;

